
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '../../../integrations/supabase/client';
import { BookingData } from '../BookingWizard';
import { MapPin, Calendar, Clock, Users, Briefcase, Car, RotateCw, AlertTriangle } from 'lucide-react';
import ConfirmationPopup from '../ConfirmationPopup';

interface SummaryStepProps {
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
}

const SummaryStep = ({ bookingData }: SummaryStepProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  const calculateTotal = () => {
    if (!bookingData.selectedVehicle || !bookingData.routeInfo) return 0;
    let total = bookingData.routeInfo.distance * bookingData.selectedVehicle.price_per_km;
    if (bookingData.isRoundTrip) {
      total *= 2;
    }
    return total;
  };

  const createBooking = useMutation({
    mutationFn: async () => {
      if (!bookingData.pickup || !bookingData.destination || !bookingData.routeInfo || !bookingData.selectedVehicle) {
        throw new Error('Missing required booking information');
      }

      const scheduledTime = new Date(`${bookingData.date}T${bookingData.time}`);
      const total = calculateTotal();
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('You must be logged in to create a booking');
      }

      const payload = {
        user_id: user.id,
        vehicle_id: bookingData.selectedVehicle.id,
        pickup_location: bookingData.pickup.address,
        dropoff_location: bookingData.destination.address,
        pickup_date: scheduledTime.toISOString(),
        passenger_name: bookingData.customerName || '',
        passenger_email: bookingData.customerEmail || '',
        passenger_phone: bookingData.customerPhone || '',
        passenger_count: bookingData.passengers,
        distance_km: bookingData.routeInfo.distance,
        total_price: total,
        status: 'pending' as const,
        special_requests: bookingData.flightNumber ? `Flight: ${bookingData.flightNumber}` : null,
      };

      console.log('Sending booking payload:', payload);
      
      const { data, error } = await supabase
        .from('bookings')
        .insert([payload])
        .select();

      if (error) {
        console.error('Detailed booking error:', error);
        throw new Error(`Booking failed: ${error.message}`);
      }
      
      return data;
    },
    onSuccess: () => {
      setBookingError(null);
      setShowConfirmation(true);
    },
    onError: (error: Error) => {
      console.error('Booking error:', error);
      setBookingError(error.message);
    }
  });

  return (
    <>
      <div className="space-y-6">
        {bookingError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div className="text-sm">{bookingError}</div>
          </div>
        )}
        
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <h3 className="font-medium text-lg text-secondary-dark">Booking Summary</h3>
          
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-primary mt-1" />
              <div>
                <div className="text-sm font-medium text-gray-700">From</div>
                <div className="text-sm text-gray-600">{bookingData.pickup?.address}</div>
                <div className="text-sm font-medium text-gray-700 mt-2">To</div>
                <div className="text-sm text-gray-600">{bookingData.destination?.address}</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-primary" />
              <div className="text-sm text-gray-600">{bookingData.date}</div>
            </div>

            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-primary" />
              <div className="text-sm text-gray-600">{bookingData.time}</div>
            </div>

            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-primary" />
              <div className="text-sm text-gray-600">
                {bookingData.passengers} passenger{bookingData.passengers !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Briefcase className="w-5 h-5 text-primary" />
              <div className="text-sm text-gray-600">
                {bookingData.luggage} luggage piece{bookingData.luggage !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Car className="w-5 h-5 text-primary" />
              <div className="text-sm text-gray-600">
                {bookingData.selectedVehicle?.name}
              </div>
            </div>

            {bookingData.isRoundTrip && (
              <div className="flex items-center space-x-3">
                <RotateCw className="w-5 h-5 text-primary" />
                <div className="text-sm text-gray-600">Round Trip</div>
              </div>
            )}
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Total Price</span>
              <span className="font-medium text-lg text-primary">€{calculateTotal().toFixed(2)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Price includes all taxes and fees</p>
          </div>
        </div>

        <button
          onClick={() => createBooking.mutate()}
          disabled={createBooking.isPending}
          className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:bg-gray-400"
        >
          {createBooking.isPending ? (
            <span className="flex items-center justify-center">
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Processing...
            </span>
          ) : 'Confirm Booking'}
        </button>

        <p className="text-sm text-gray-500 text-center">
          By confirming your booking, you agree to our terms and conditions
        </p>
      </div>

      <ConfirmationPopup 
        isOpen={showConfirmation} 
        onClose={() => setShowConfirmation(false)} 
      />
    </>
  );
};

export default SummaryStep;
